import { useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';
import {
  buildFrontendToolSchema,
  runFrontendToolHandler,
  type FrontendToolDefinition,
} from '@/hooks/useFrontendTool';
import { resolveIds } from './resolveFilterValues';

/**
 * One agent tool per filter on a list page, generated from a declarative
 * spec. Per-filter rather than one `set_filters({...})` tool on purpose: the
 * model picks a tool from its name and description, and "filter by stage" is
 * a decision it can make from the tool list alone (see useDemandAgentTools).
 *
 * Every field tool is fire-and-forget and read-only: it changes what the user
 * is looking at, never business data.
 */

export interface UrlFilterOption {
  id: string;
  label: string;
}

export interface DateRangeValue {
  from?: string;
  to?: string;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface FieldBase<S> {
  /** e.g. `filter_sales_orders_by_stage` — never a server tool's short name. */
  toolName: string;
  /** The trigger phrasing the model matches on, nothing else. */
  description: string;
  /** Human label used in error text and in the on-screen `filters` context. */
  label: string;
  /** False keeps the field out of the tool list without unmounting anything. */
  enabled?: boolean;
  /**
   * Current human-readable value for the `filters` context, or undefined when
   * the filter is not active. Receives the options so ids can become labels.
   */
  describe: (
    search: S,
    options: UrlFilterOption[] | undefined,
  ) => string | undefined;
}

interface WithOptions {
  /** Read at call time; undefined means "not loaded yet". */
  options?: () => UrlFilterOption[] | undefined;
}

export type UrlFilterFieldSpec<S> =
  | (FieldBase<S> & {
      kind: 'text';
      apply: (value: string | undefined, prev: S) => S;
    })
  | (FieldBase<S> &
      WithOptions & {
        kind: 'enum';
        apply: (value: string | undefined, prev: S) => S;
      })
  | (FieldBase<S> &
      WithOptions & {
        kind: 'multiEnum';
        apply: (values: string[] | undefined, prev: S) => S;
      })
  | (FieldBase<S> &
      WithOptions & {
        kind: 'ids';
        /** What to tell the model when a value is neither an id nor a known label. */
        resolveHint?: string;
        apply: (values: string[] | undefined, prev: S) => S;
      })
  | (FieldBase<S> & {
      kind: 'boolean';
      apply: (value: boolean | undefined, prev: S) => S;
    })
  | (FieldBase<S> & {
      kind: 'dateRange';
      /** Named ranges the model may use instead of dates ("thisMonth"). */
      resolvePreset?: (preset: string) => DateRangeValue | undefined;
      presetNames?: readonly string[];
      apply: (range: DateRangeValue | undefined, prev: S) => S;
    })
  | (FieldBase<S> &
      WithOptions & {
        kind: 'sort';
        apply: (
          sort: { by: string; direction: 'asc' | 'desc' } | undefined,
          prev: S,
        ) => S;
      });

export interface UrlFilterToolsSpec<S> {
  /** The committed search/filter state (from the router). */
  search: S;
  /** The page's own setter (navigate with replace, resets paging as it sees fit). */
  onChange: (next: S) => void;
  fields: UrlFilterFieldSpec<S>[];
  clearTool?: {
    toolName: string;
    description: string;
    clear: (prev: S) => S;
  };
  pageTool?: {
    toolName: string;
    description: string;
    apply: (page: number, prev: S) => S;
  };
}

export interface UrlFilterTools {
  tools: FrontendToolDefinition[];
  /** label → human value, for the surface's `filters` context. */
  activeFilters: Record<string, string>;
}

function requireOptions(
  read: (() => UrlFilterOption[] | undefined) | undefined,
  label: string,
): UrlFilterOption[] {
  const options = read?.();
  if (!options) {
    throw new Error(
      `The page has not loaded its ${label} options yet. Ask the user to retry in a moment.`,
    );
  }
  return options;
}

function toNamed(options: UrlFilterOption[]) {
  return options.map((option) => ({ id: option.id, name: option.label }));
}

/** Ids pass through; labels resolve against the options; anything else errors with the hint. */
function resolveIdValues(
  values: string[],
  options: UrlFilterOption[] | undefined,
  label: string,
  hint: string | undefined,
): string[] {
  const ids: string[] = [];
  const unknown: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (UUID_RE.test(trimmed)) {
      ids.push(trimmed);
      continue;
    }
    const match = options?.find(
      (option) => option.label.trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (match) ids.push(match.id);
    else unknown.push(value);
  }
  if (unknown.length > 0) {
    throw new Error(
      `No ${label} id for ${unknown.map((v) => `"${v}"`).join(', ')}. ${
        hint ??
        `Resolve the name to an id first (e.g. with a search tool), then call this tool with ids.`
      }`,
    );
  }
  return ids;
}

const emptyValuesSchema = z.object({
  values: z
    .array(z.string())
    .describe('Values to filter by. An empty array clears this filter.'),
});
const textSchema = z.object({
  value: z.string().describe('Text to filter by. Empty clears this filter.'),
});
const booleanSchema = z.object({
  value: z
    .boolean()
    .nullable()
    .describe('True or false to filter; null clears this filter.'),
});
const pageSchema = z.object({
  page: z.number().int().min(1).describe('1-based page number'),
});

/**
 * A field's tool minus the closure that commits it: the parameter schema the
 * model sees and a pure `run` from (args, previous state, latest field) to the
 * next state. Free of refs on purpose, so the hook can attach the ref-reading
 * `execute` itself (see the note in `useUrlFilterTools`).
 */
interface FieldTool<S> {
  parameters: z.ZodType;
  run: (args: unknown, prev: S, field: UrlFilterFieldSpec<S>) => S;
}

/**
 * Field spec → parameters + pure run. `snapshot` fixes what the model sees
 * (name, description, schema); `field` is the LATEST spec at call time, so
 * option readers and apply functions never go stale.
 */
function buildFieldTool<S>(snapshot: UrlFilterFieldSpec<S>): FieldTool<S> {
  const tool = <P extends z.ZodType>(
    parameters: P,
    run: (args: z.infer<P>, prev: S, field: UrlFilterFieldSpec<S>) => S,
  ): FieldTool<S> => ({
    parameters,
    run: (args, prev, field) => run(parameters.parse(args), prev, field),
  });
  switch (snapshot.kind) {
    case 'text':
      return tool(textSchema, ({ value }, prev, field) =>
        field.kind === 'text'
          ? field.apply(value.trim() || undefined, prev)
          : prev,
      );
    case 'enum':
      return tool(textSchema, ({ value }, prev, field) => {
        if (field.kind !== 'enum') return prev;
        const trimmed = value.trim();
        if (!trimmed) return field.apply(undefined, prev);
        const options = requireOptions(field.options, field.label);
        const [id] = resolveIds([trimmed], toNamed(options), field.label);
        return field.apply(id, prev);
      });
    case 'multiEnum':
      return tool(emptyValuesSchema, ({ values }, prev, field) => {
        if (field.kind !== 'multiEnum') return prev;
        if (values.length === 0) return field.apply(undefined, prev);
        const options = requireOptions(field.options, field.label);
        return field.apply(
          resolveIds(values, toNamed(options), field.label),
          prev,
        );
      });
    case 'ids':
      return tool(emptyValuesSchema, ({ values }, prev, field) => {
        if (field.kind !== 'ids') return prev;
        if (values.length === 0) return field.apply(undefined, prev);
        return field.apply(
          resolveIdValues(
            values,
            field.options?.(),
            field.label,
            field.resolveHint,
          ),
          prev,
        );
      });
    case 'boolean':
      return tool(booleanSchema, ({ value }, prev, field) =>
        field.kind === 'boolean' ? field.apply(value ?? undefined, prev) : prev,
      );
    case 'dateRange': {
      const presetNames = snapshot.presetNames ?? [];
      const schema = z.object({
        from: z
          .string()
          .optional()
          .describe('Inclusive start date, YYYY-MM-DD'),
        to: z.string().optional().describe('Inclusive end date, YYYY-MM-DD'),
        preset: z
          .string()
          .optional()
          .describe(
            presetNames.length > 0
              ? `Named range instead of dates: ${presetNames.join(', ')}`
              : 'Named range instead of dates',
          ),
      });
      return tool(schema, ({ from, to, preset }, prev, field) => {
        if (field.kind !== 'dateRange') return prev;
        if (preset) {
          const range = field.resolvePreset?.(preset);
          if (!range) {
            throw new Error(
              `Unknown preset "${preset}". Available: ${presetNames.join(', ') || 'none'}.`,
            );
          }
          return field.apply(range, prev);
        }
        if (!from && !to) return field.apply(undefined, prev);
        return field.apply({ from, to }, prev);
      });
    }
    case 'sort': {
      const schema = z.object({
        by: z.string().describe('Column to sort by'),
        direction: z.enum(['asc', 'desc']).optional(),
      });
      return tool(schema, ({ by, direction }, prev, field) => {
        if (field.kind !== 'sort') return prev;
        const options = requireOptions(field.options, field.label);
        const [id] = resolveIds([by], toNamed(options), field.label);
        return field.apply({ by: id, direction: direction ?? 'desc' }, prev);
      });
    }
  }
}

function describeAll<S>(
  fields: UrlFilterFieldSpec<S>[],
  search: S,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const field of fields) {
    const options =
      'options' in field && field.options ? field.options() : undefined;
    const value = field.describe(search, options);
    if (value) out[field.label] = value;
  }
  return out;
}

function toJsonSchema(schema: z.ZodType): Record<string, unknown> {
  return z.toJSONSchema(schema) as Record<string, unknown>;
}

const READ_ONLY_UI_TOOL = { awaitable: false, readOnlyHint: true } as const;

export function useUrlFilterTools<S>(
  spec: UrlFilterToolsSpec<S>,
): UrlFilterTools {
  // The model can call several tools before React re-renders and the router
  // hands back a new search, so each call builds on this ref rather than on
  // the `search` prop; every render resyncs it from the committed state (the
  // rationale is spelled out in useDemandAgentTools).
  const liveRef = useRef(spec.search);
  useEffect(() => {
    liveRef.current = spec.search;
  });
  const specRef = useRef(spec);
  useEffect(() => {
    specRef.current = spec;
  });

  // Identity-keyed on what the model sees (names, descriptions, kinds, enabled
  // flags), not on the option/apply closures, which read through the ref.
  // Manual useMemo on purpose: the tools array feeds useAgentModule's
  // registration effect, so it must only change when the tool list changes
  // (same reasoning as useFrontendTool's schema key).
  const signature = JSON.stringify([
    spec.fields.map((field) => [
      field.toolName,
      field.description,
      field.kind,
      field.label,
      field.enabled ?? true,
      field.kind === 'dateRange' ? (field.presetNames ?? []) : null,
    ]),
    spec.clearTool
      ? [spec.clearTool.toolName, spec.clearTool.description]
      : null,
    spec.pageTool ? [spec.pageTool.toolName, spec.pageTool.description] : null,
  ]);

  const tools = useMemo(() => {
    // The closures below read the refs only when the agent calls a tool, never
    // during render, and are stored in object literals rather than handed to a
    // helper: the React Compiler's ref rule counts a ref-reading function
    // passed to a non-hook call during render as a render-time access.
    /** Commits a change against the live state and reports the new filters. */
    const commit = (next: S) => {
      liveRef.current = next;
      specRef.current.onChange(next);
      return { applied: describeAll(specRef.current.fields, next) };
    };
    const fieldTools = spec.fields.map(
      (snapshot, index): FrontendToolDefinition => {
        const { parameters, run } = buildFieldTool(snapshot);
        const { toolName: name, description } = snapshot;
        return {
          name,
          description,
          awaitable: false,
          enabled: snapshot.enabled ?? true,
          schema: buildFrontendToolSchema({
            name,
            description,
            parameters: toJsonSchema(parameters),
            ...READ_ONLY_UI_TOOL,
          }),
          execute: (args: unknown) =>
            runFrontendToolHandler(
              (input) =>
                commit(
                  run(input, liveRef.current, specRef.current.fields[index]),
                ),
              args,
            ),
        };
      },
    );
    const { clearTool, pageTool } = spec;
    const clearTools: FrontendToolDefinition[] = clearTool
      ? [
          {
            name: clearTool.toolName,
            description: clearTool.description,
            awaitable: false,
            enabled: true,
            schema: buildFrontendToolSchema({
              name: clearTool.toolName,
              description: clearTool.description,
              parameters: toJsonSchema(z.object({})),
              ...READ_ONLY_UI_TOOL,
            }),
            execute: () =>
              runFrontendToolHandler(
                () =>
                  commit(
                    specRef.current.clearTool?.clear(liveRef.current) ??
                      liveRef.current,
                  ),
                undefined,
              ),
          },
        ]
      : [];
    const pageTools: FrontendToolDefinition[] = pageTool
      ? [
          {
            name: pageTool.toolName,
            description: pageTool.description,
            awaitable: false,
            enabled: true,
            schema: buildFrontendToolSchema({
              name: pageTool.toolName,
              description: pageTool.description,
              parameters: toJsonSchema(pageSchema),
              ...READ_ONLY_UI_TOOL,
            }),
            execute: (args: unknown) =>
              runFrontendToolHandler((input) => {
                const { page } = pageSchema.parse(input);
                return commit(
                  specRef.current.pageTool?.apply(page, liveRef.current) ??
                    liveRef.current,
                );
              }, args),
          },
        ]
      : [];
    return [...fieldTools, ...clearTools, ...pageTools];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return { tools, activeFilters: describeAll(spec.fields, spec.search) };
}
